import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { API_BASE } from "../../lib/api";
import { setCurrentCustomer } from "../../lib/customerSession";
import GoogleButton from "../../components/onboarding/GoogleButton";
import AuthBrandPanel from "../../components/onboarding/AuthBrandPanel";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Command Center is the default post-login landing page (2026-11). If the
  // user was bounced here from a protected route they opened directly
  // (App.js's route guard sets `state: { from }`), honor that deep link
  // instead — the SAME protected-route guard re-runs on that path's render,
  // so nothing here needs to duplicate permission checks.
  const redirectTarget = location.state?.from || "/command-center";

  // Sign-in form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [successBanner, setSuccessBanner] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot-password section state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent]   = useState(false);
  const [resetCode, setResetCode]   = useState("");
  const [newPwd, setNewPwd]         = useState("");
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [resetMsg, setResetMsg]     = useState("");
  const [resetError, setResetError] = useState("");
  const [sendingReset, setSendingReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const forgotRef = useRef(null);

  // Auth guard — already logged in → Command Center (or the deep link)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("current_customer");
      if (raw && raw !== "null" && raw !== "undefined") {
        navigate(redirectTarget, { replace: true });
      }
    } catch (_e) { /* ignore */ }
  }, [navigate, redirectTarget]);

  const validate = () => {
    const e = {};
    if (!email.trim()) e.email = "Please enter your email.";
    else if (!EMAIL_RE.test(email.trim())) e.email = "Please enter a valid email.";
    if (!password) e.password = "Please enter your password.";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setBanner("");
    setSuccessBanner("");
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/onboarding/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      if (res.ok) {
        const data = await res.json();
        // Persist + broadcast the session synchronously so App's
        // useCurrentCustomer + CustomerDataProvider re-read it BEFORE the
        // route change lands — otherwise the route guard sees a stale
        // "visitor" state and bounces back to /login (the multi-attempt bug).
        setCurrentCustomer(data);
        navigate(redirectTarget, { replace: true });
        return;
      }
      let detail = "";
      try { const j = await res.json(); detail = j?.detail || ""; } catch (_e) {}
      if (res.status === 400 && detail === "invalid_credentials") {
        setErrors({ email: "Invalid email or password. Please try again." });
      } else {
        setBanner("Something went wrong. Please try again.");
      }
    } catch (_e) {
      setBanner("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openForgot = () => {
    setForgotOpen(true);
    setResetEmail(email || "");
    setResetMsg(""); setResetError("");
    // Smooth scroll the section into view after it renders
    setTimeout(() => forgotRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };

  const sendReset = async () => {
    setResetMsg(""); setResetError("");
    if (!EMAIL_RE.test((resetEmail || "").trim())) {
      setResetError("Please enter a valid email.");
      return;
    }
    setSendingReset(true);
    try {
      const res = await fetch(`${API_BASE}/onboarding/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail.trim().toLowerCase() }),
      });
      if (res.ok) {
        setResetSent(true);
        setResetMsg("Reset code sent to your email. Check your inbox.");
      } else {
        setResetError("Could not send code right now. Please try again.");
      }
    } catch (_e) {
      setResetError("Could not send code right now. Please try again.");
    } finally {
      setSendingReset(false);
    }
  };

  const submitReset = async () => {
    setResetError("");
    if (!/^\d{6}$/.test(resetCode.trim())) {
      setResetError("Enter the 6-digit reset code from your email.");
      return;
    }
    if (newPwd.length < 8) {
      setResetError("New password must be at least 8 characters.");
      return;
    }
    setResetting(true);
    try {
      const res = await fetch(`${API_BASE}/onboarding/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: resetEmail.trim().toLowerCase(),
          reset_code: resetCode.trim(),
          new_password: newPwd,
        }),
      });
      if (res.ok) {
        // Reset succeeded — collapse the section and show the success banner.
        setForgotOpen(false);
        setResetSent(false);
        setResetCode("");
        setNewPwd("");
        setPassword("");
        setSuccessBanner("Password reset successful. Please sign in.");
        setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
      } else {
        let detail = "";
        try { const j = await res.json(); detail = j?.detail || ""; } catch (_e) {}
        if (detail === "password_too_short") setResetError("New password must be at least 8 characters.");
        else if (detail === "invalid_or_expired_code") setResetError("Code is incorrect or expired. Send a new one.");
        else setResetError("Could not reset password. Please try again.");
      }
    } catch (_e) {
      setResetError("Could not reset password. Please try again.");
    } finally {
      setResetting(false);
    }
  };

  const inputCls =
    "h-12 w-full rounded border border-ink-200 bg-white px-4 font-sans text-[15px] text-ink-950 placeholder:text-ink-400 shadow-sm outline-none transition-colors duration-150 focus:border-blue-800 focus:ring-4 focus:ring-blue-800/10";
  const labelCls = "mb-1.5 block font-sans text-[13px] font-medium text-ink-700";

  return (
    <div data-testid="login-page" className="flex min-h-screen w-full flex-col overflow-hidden bg-white md:flex-row">
      <AuthBrandPanel testidPrefix="login" />

      {/* RIGHT PANE — sign-in form. Logic untouched; chrome restyled. */}
      <main className="relative flex w-full items-center justify-center bg-white p-6 sm:p-12 md:w-[54%] lg:w-[50%] lg:p-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.15 }}
          className="w-full max-w-[420px]"
        >
          {/* Mobile logo (visible only when left pane is hidden) */}
          <div className="mb-8 flex justify-center md:hidden">
            <img src="/c-ore-logo.png" alt="C-ORE" draggable="false"
                 style={{ height: 38, width: 138, objectFit: "contain" }} />
          </div>

          <AnimatePresence>
            {successBanner && (
              <motion.div
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                data-testid="login-success-banner"
                className="mb-4 flex items-start gap-2 rounded border border-status-onTrack-border bg-status-onTrack-bg p-3.5 font-sans text-[13px] text-status-onTrack-text"
              >
                {successBanner}
              </motion.div>
            )}
            {banner && (
              <motion.div
                initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                data-testid="login-banner"
                className="mb-4 flex items-start gap-2 rounded border border-status-critical-border bg-status-critical-bg p-3.5 font-sans text-[13px] text-status-critical-text"
              >
                {banner}
              </motion.div>
            )}
          </AnimatePresence>

          <h2 className="font-display text-[28px] font-semibold tracking-tight text-ink-950">Welcome back</h2>
          <p className="mt-1.5 font-sans text-[14px] text-ink-500">Sign in to your C-ORE dashboard</p>

          <div className="mt-7">
            {/* Emergent-managed Google Sign-in. See /app/auth_testing.md. */}
            <GoogleButton testId="login-google-button" redirectPath={redirectTarget} />
          </div>

          <div className="my-6 flex items-center gap-3 font-sans text-[11.5px] uppercase tracking-widest text-ink-400">
            <span className="h-px flex-1 bg-ink-200" /> OR <span className="h-px flex-1 bg-ink-200" />
          </div>

          <form onSubmit={onSubmit} noValidate>
            {/* Email */}
            <div className="mb-4">
              <label className={labelCls} htmlFor="login-email">Business Email</label>
              <input
                data-testid="login-email"
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors((x) => ({ ...x, email: undefined })); }}
                placeholder="you@yourcompany.com"
                autoComplete="email"
                className={inputCls}
              />
              {errors.email && <p data-testid="login-error-email" className="mt-1.5 font-sans text-[12px] text-status-critical-text">{errors.email}</p>}
            </div>

            {/* Password */}
            <div className="mb-5">
              <div className="flex items-baseline justify-between">
                <label className={labelCls} htmlFor="login-password">Password</label>
                <button
                  data-testid="login-forgot-link"
                  type="button"
                  onClick={openForgot}
                  className="font-sans text-[12.5px] font-semibold text-blue-800 transition-colors hover:text-blue-900"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <input
                  data-testid="login-password"
                  id="login-password"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors((x) => ({ ...x, password: undefined })); }}
                  placeholder="Your password"
                  autoComplete="current-password"
                  className={inputCls}
                />
                <button
                  data-testid="login-toggle-password"
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-ink-400 transition-colors hover:text-blue-800"
                  aria-label={showPwd ? "Hide password" : "Show password"}
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <p data-testid="login-error-password" className="mt-1.5 font-sans text-[12px] text-status-critical-text">{errors.password}</p>}
            </div>

            <motion.button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              whileHover={!loading ? { y: -1 } : {}}
              whileTap={!loading ? { y: 0 } : {}}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-blue-800 font-sans text-[15px] font-semibold text-white shadow-[0_4px_14px_rgba(3,79,175,0.25)] transition-colors duration-150 hover:bg-blue-900 hover:shadow-[0_8px_20px_rgba(3,79,175,0.32)] disabled:cursor-default disabled:opacity-80"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Signing in…" : "Sign In"}
              {!loading && <ArrowRight size={15} />}
            </motion.button>
          </form>

          {/* Forgot password section — collapsed by default. */}
          <div ref={forgotRef} />
          <AnimatePresence initial={false}>
            {forgotOpen && (
              <motion.div
                data-testid="forgot-section"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: [0.2, 0.6, 0.3, 1] }}
                className="overflow-hidden"
              >
                <div className="mt-5 border-t border-ink-100 pt-5">
                  <h3 className="font-display text-[15px] font-semibold text-ink-950">Reset your password</h3>
                  <p className="mt-1 mb-3.5 font-sans text-[12.5px] text-ink-500">
                    We'll email a 6-digit reset code. The code is valid for 30 minutes.
                  </p>

                  <label className={labelCls} htmlFor="reset-email">Email</label>
                  <input
                    data-testid="reset-email"
                    id="reset-email"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className={inputCls}
                    placeholder="you@yourcompany.com"
                  />

                  <motion.button
                    data-testid="reset-send"
                    type="button"
                    onClick={sendReset}
                    disabled={sendingReset}
                    whileHover={!sendingReset ? { y: -1 } : {}}
                    className="mt-3.5 h-11 w-full rounded-full bg-blue-800 font-sans text-[14px] font-semibold text-white shadow-sm transition-colors duration-150 hover:bg-blue-900 disabled:cursor-default disabled:opacity-80"
                  >
                    {sendingReset ? "Sending…" : (resetSent ? "Resend Reset Code" : "Send Reset Code")}
                  </motion.button>

                  {resetMsg && <p data-testid="reset-info" className="mt-2.5 font-sans text-[12.5px] text-status-onTrack-text">{resetMsg}</p>}
                  {resetError && <p data-testid="reset-error" className="mt-2.5 font-sans text-[12px] text-status-critical-text">{resetError}</p>}

                  {resetSent && (
                    <div className="mt-4">
                      <label className={labelCls} htmlFor="reset-code">6-digit reset code</label>
                      <input
                        data-testid="reset-code"
                        id="reset-code"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={resetCode}
                        onChange={(e) => setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="••••••"
                        className={`${inputCls} text-center text-[18px] font-bold tracking-[6px]`}
                      />

                      <label className={`${labelCls} mt-3.5`} htmlFor="reset-new-pwd">New Password</label>
                      <div className="relative">
                        <input
                          data-testid="reset-new-password"
                          id="reset-new-pwd"
                          type={showNewPwd ? "text" : "password"}
                          value={newPwd}
                          onChange={(e) => setNewPwd(e.target.value)}
                          placeholder="At least 8 characters"
                          autoComplete="new-password"
                          className={inputCls}
                        />
                        <button
                          data-testid="reset-toggle-password"
                          type="button"
                          onClick={() => setShowNewPwd((v) => !v)}
                          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-ink-400 transition-colors hover:text-blue-800"
                          aria-label={showNewPwd ? "Hide password" : "Show password"}
                        >
                          {showNewPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>

                      <motion.button
                        data-testid="reset-confirm"
                        type="button"
                        onClick={submitReset}
                        disabled={resetting}
                        whileHover={!resetting ? { y: -1 } : {}}
                        className="mt-4 h-11 w-full rounded-full bg-blue-800 font-sans text-[14px] font-semibold text-white shadow-sm transition-colors duration-150 hover:bg-blue-900 disabled:cursor-default disabled:opacity-80"
                      >
                        {resetting ? "Resetting…" : "Confirm Reset"}
                      </motion.button>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="my-6 flex items-center gap-3 font-sans text-[11.5px] uppercase tracking-widest text-ink-400">
            <span className="h-px flex-1 bg-ink-200" /> OR <span className="h-px flex-1 bg-ink-200" />
          </div>

          <p className="text-center font-sans text-[13.5px] text-ink-500">
            New to C-ORE?{" "}
            <span
              data-testid="login-go-signup"
              onClick={() => navigate("/signup")}
              className="cursor-pointer font-semibold text-blue-800 hover:text-blue-900"
            >
              Start free trial
            </span>
          </p>
        </motion.div>
      </main>
    </div>
  );
}
